import { analyzeCommunityFiles } from 'apis/CommunityAPI';
import { convertToBase64, hasUploadFileIdentity } from 'utils/helpers/files';

type ShowMessageType = 'warning' | 'error';

type AnalyzeDocumentFilesArgs = {
  files: any[];
  communityId: number;
  source: string;
  analyzingUids: Set<string>;
  getDocuments: () => any[];
  setDocuments: (documents: any[]) => void;
  showMessage: (type: ShowMessageType, message: string) => void;
};

export const normalizeDocuments = (documents: any[]) =>
  (documents ?? []).filter((file: any) => hasUploadFileIdentity(file));

export const clearDocumentAnalysisErrors = (documents: any[]) =>
  normalizeDocuments(documents).map((file: any) =>
    file?.file_category_ids?.length
      ? {
          ...file,
          analysis_error_message: undefined,
        }
      : file
  );

export const hasPendingDocumentAnalysis = (documents: any[]) =>
  normalizeDocuments(documents).some((file: any) => Boolean(file?.analysis_pending));

const getDocumentAnalysisKey = (file: any) => {
  if (!file || typeof file !== 'object') {
    return undefined;
  }

  return file.uid ?? file.id ?? undefined;
};

export const analyzeDocumentFiles = async ({
  files,
  communityId,
  source,
  analyzingUids,
  getDocuments,
  setDocuments,
  showMessage,
}: AnalyzeDocumentFilesArgs) => {
  const filesToAnalyze = files.filter(
    (file) =>
      !file.id &&
      Boolean(file.originFileObj) &&
      Boolean(getDocumentAnalysisKey(file)) &&
      file.status === 'done' &&
      !file.analysis_pending &&
      !file.analysis_run_id &&
      !file.file_category_ids?.length &&
      !file.suggested_file_category_ids?.length &&
      !analyzingUids.has(getDocumentAnalysisKey(file))
  );

  if (!filesToAnalyze.length) {
    return;
  }

  const pendingKeys = filesToAnalyze.map((file) => getDocumentAnalysisKey(file)).filter(Boolean);
  pendingKeys.forEach((key) => analyzingUids.add(key));

  setDocuments(
    files.map((file) =>
      pendingKeys.includes(getDocumentAnalysisKey(file))
        ? {
            ...file,
            analysis_pending: true,
          }
        : file
    )
  );

  try {
    const analyzePayload = await Promise.all(
      filesToAnalyze.map(async (file) => ({
        name: file.name ?? 'Untitled',
        file: {
          name: file.name ?? 'Untitled',
          data: await convertToBase64(file.originFileObj),
        },
      }))
    );

    const response = await analyzeCommunityFiles(communityId, {
      source,
      files: analyzePayload,
    });

    const resultsByKey = new Map(
      filesToAnalyze.map((file, index) => [getDocumentAnalysisKey(file), response?.results?.[index] ?? null])
    );
    const hasAnalysisFailures = (response?.results ?? []).some((result: any) => Boolean(result?.error_message));

    setDocuments(
      getDocuments().map((file: any) => {
        const result = resultsByKey.get(getDocumentAnalysisKey(file));

        if (!result) {
          return file;
        }

        const suggestedCategoryIds = (result.suggested_file_categories ?? []).map((category: any) => category.id);

        return {
          ...file,
          analysis_pending: false,
          analysis_run_id: result.analysis_run_id,
          summary: result.summary ?? '',
          analysis_error_message: result.error_message ?? undefined,
          suggested_file_category_ids: suggestedCategoryIds,
          file_category_ids: file.file_category_ids?.length ? file.file_category_ids : suggestedCategoryIds,
        };
      })
    );

    if (hasAnalysisFailures) {
      showMessage('warning', 'Some files could not be analyzed. Select a category manually before saving.');
    }
  } catch (error) {
    showMessage('error', 'Trouble analyzing uploaded files.');
    setDocuments(
      getDocuments().map((file: any) =>
        pendingKeys.includes(getDocumentAnalysisKey(file))
          ? {
              ...file,
              analysis_pending: false,
            }
          : file
      )
    );
  } finally {
    pendingKeys.forEach((key) => analyzingUids.delete(key));
  }
};
